import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, Check, Loader } from "lucide-react";
import apiFetch from "../apiFetch";
import "../styles/joinClassPage.css";
import PageHeader from "../components/PageHeader";

export default function JoinClassPage() {
  const [code,         setCode]         = useState("");
  const [connection,   setConnection]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [success,      setSuccess]      = useState(null);
  const [error,        setError]        = useState("");

  useEffect(() => {
    document.title = "Join a Class — Owllet Reading Quest";
    apiFetch("/api/student/connection")
      .then(r => r.ok ? r.json() : null)
      .then(data => { setConnection(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDisconnect = async () => {
    if (!window.confirm("Leave your current class?")) return;
    await apiFetch("/api/student/disconnect", { method: "DELETE" });
    setConnection({ status: "none" });
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await apiFetch("/api/student/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to connect"); setSubmitting(false); return; }
      setSuccess({ teacherName: data.teacherName });
      setConnection({ status: "pending", teacherName: data.teacherName });
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="jc-page">
      <PageHeader
        pageTitle="Join a Class"
        pageSubtitle="Got a class code? Enter it below and get connected to your teacher in seconds"
      />

      <main className="jc-main">
        {loading ? (
          <div className="jc-loading"><Loader size={24} className="jc-spin" /> Loading…</div>
        ) : connection?.status === "accepted" ? (
          <div className="jc-card">
            <div className="jc-connected-icon"><Check size={32} color="#22c55e" /></div>
            <h2 className="jc-card-title">You're in {connection.teacherName}'s class</h2>
            <p className="jc-card-sub">Your teacher has accepted you. Check your homework tab for assignments.</p>
            <div className="jc-card-actions">
              <Link to="/homework" className="jc-btn jc-btn--primary">View Homework</Link>
              <button className="jc-btn jc-btn--ghost jc-btn--sm" onClick={handleDisconnect}>Leave Class</button>
            </div>
          </div>
        ) : connection?.status === "pending" && !success ? (
          <div className="jc-card">
            <div className="jc-pending-icon"><Users size={32} color="#f59e0b" /></div>
            <h2 className="jc-card-title">Request pending</h2>
            <p className="jc-card-sub">Your request to join {connection.teacherName}'s class is awaiting approval.</p>
            <div className="jc-card-actions">
              <button className="jc-btn jc-btn--ghost jc-btn--sm" onClick={handleDisconnect}>Cancel Request</button>
            </div>
          </div>
        ) : success ? (
          <div className="jc-card">
            <div className="jc-connected-icon"><Check size={32} color="#22c55e" /></div>
            <h2 className="jc-card-title">Request sent!</h2>
            <p className="jc-card-sub">Your request to join {success.teacherName}'s class has been sent. They'll receive a notification and can accept you from their dashboard.</p>
            <div className="jc-card-actions">
              <Link to="/" className="jc-btn jc-btn--primary">Go Home</Link>
            </div>
          </div>
        ) : (
          <div className="jc-card">
            <div className="jc-icon"><Users size={40} /></div>
            <h2 className="jc-card-title">Enter your teacher's code</h2>
            <p className="jc-card-sub">Your teacher will share a class code with you. Enter it below to send a connection request.</p>
            <form className="jc-form" onSubmit={handleSubmit}>
              <input
                className="jc-input"
                value={code}
                onChange={e => { setCode(e.target.value.toUpperCase()); setError(""); }}
                placeholder="e.g. SMITH-1234"
                maxLength={30}
                autoFocus
                autoComplete="off"
              />
              {error && <p className="jc-error">{error}</p>}
              <button type="submit" className="jc-btn jc-btn--primary" disabled={submitting || !code.trim()}>
                {submitting ? "Sending…" : "Send Request"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
